"use client";

import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
} from "@ionic/react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  LinearProgress,
  Fade,
} from "@mui/material";
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subYears } from 'date-fns';
import axios from "axios";
import { requestID, baseUrl, config } from "../../../../common/utils";

// Define types based on your API response structure
interface ProfitLossItem {
  revenue: number;
  cost_of_services: number;
  administrative_expenses: number;
  operating_income: number;
  finance_income: number;
  finance_cost: number;
  income_tax: number;
  comprehensive_income: number;
  cash_equivalent: number;
}

interface ProfitLossResponse {
  status: boolean;
  data: ProfitLossItem;
  "data.previous"?: ProfitLossItem;
}

const ProfitAndLoss: React.FC = () => {
  // State management
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentData, setCurrentData] = useState<ProfitLossItem | null>(null);
  const [previousData, setPreviousData] = useState<ProfitLossItem | null>(null);

  // Helper functions
  const formatCurrency = (value: number) => {
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    
    return (
      <span style={{ color: isNegative ? '#d32f2f' : 'inherit' }}>
        {isNegative ? '-' : ''}
        {new Intl.NumberFormat('en-NG', {
          style: 'currency',
          currency: 'NGN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(absValue)}
      </span>
    );
  };

  const getTrendIcon = (current: number, previous: number) => {
    const change = current - previous;
    if (change > 0) return <TrendingUpIcon color="success" fontSize="small" />;
    if (change < 0) return <TrendingDownIcon color="error" fontSize="small" />;
    return <TrendingFlatIcon color="action" fontSize="small" />;
  };

  const calculateChange = (current: number, previous: number) => {
    if (previous === 0) return { value: current, percent: 0 };
    const change = current - previous;
    const percent = (change / Math.abs(previous)) * 100;
    return { value: change, percent };
  };

  // Format date for display
  const formatDateDisplay = (date: Date) => {
    const day = format(date, 'do');
    const monthYear = format(date, 'MMMM yyyy');
    return `${day} ${monthYear}`;
  };

  const formatDateAPI = (date: Date) => {
    return format(date, 'yyyy-MM-dd');
  };

  // CORRECTED CALCULATION FUNCTIONS - USING SUMS ONLY
  const calculateDerivedValues = () => {
    if (!currentData || !previousData) return null;

    // Current period calculations - ALL SUMS
    const currentGrossProfit = currentData.revenue + currentData.cost_of_services;
    const previousGrossProfit = previousData.revenue + previousData.cost_of_services;

    // Operating Profit = Gross Profit + Other Operating Income + Administrative Expenses
    const currentOperatingProfit = currentGrossProfit + currentData.operating_income + currentData.administrative_expenses;
    const previousOperatingProfit = previousGrossProfit + previousData.operating_income + previousData.administrative_expenses;

    // Net Finance Cost = Finance Cost + Finance Income 
    const currentNetFinanceCost = currentData.finance_cost + currentData.finance_income;
    const previousNetFinanceCost = previousData.finance_cost + previousData.finance_income;

    // Profit Before Tax = Operating Profit + Net Finance Cost
    const currentProfitBeforeTax = currentOperatingProfit + currentNetFinanceCost;
    const previousProfitBeforeTax = previousOperatingProfit + previousNetFinanceCost;

    // Profit After Tax = Profit Before Tax + Income Tax 
    const currentProfitAfterTax = currentProfitBeforeTax + currentData.income_tax;
    const previousProfitAfterTax = previousProfitBeforeTax + previousData.income_tax;

    // Total Comprehensive Income = Profit After Tax + Other Comprehensive Income
    const currentTotalComprehensiveIncome = currentProfitAfterTax + currentData.comprehensive_income;
    const previousTotalComprehensiveIncome = previousProfitAfterTax + previousData.comprehensive_income;

    return {
      current: {
        grossProfit: currentGrossProfit,
        operatingProfit: currentOperatingProfit,
        netFinanceCost: currentNetFinanceCost,
        profitBeforeTax: currentProfitBeforeTax,
        profitAfterTax: currentProfitAfterTax,
        totalComprehensiveIncome: currentTotalComprehensiveIncome
      },
      previous: {
        grossProfit: previousGrossProfit,
        operatingProfit: previousOperatingProfit,
        netFinanceCost: previousNetFinanceCost,
        profitBeforeTax: previousProfitBeforeTax,
        profitAfterTax: previousProfitAfterTax,
        totalComprehensiveIncome: previousTotalComprehensiveIncome
      }
    };
  };

  const getProfitLossData = async () => {
    setLoading(true);
    setError(null);

    const formData = {
      requestID: requestID(),
      coa_ids: ["coa_6970f88b524944.37142009", "coa_6970fa41446088.93886338"],
      as_at_date: formatDateAPI(selectedDate),
      endDate: formatDateAPI(selectedDate)
    };

    try {
      const response = await axios.post<ProfitLossResponse>(
        baseUrl() + "Geacloud_ChartOfAccounts/getNetBalances_ProfitandLoss",
        formData,
        config
      );

      if (response.data.status) {
        setCurrentData(response.data.data);
        if (response.data["data.previous"]) {
          setPreviousData(response.data["data.previous"]);
        }
      } else {
        setError("Failed to fetch profit and loss data");
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch profit and loss data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = (format: 'pdf' | 'excel') => {
    // Export logic here
    console.log(`Exporting as ${format}`);
  };

  // Handle date change
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  useEffect(() => {
    getProfitLossData();
  }, [selectedDate]);

  // Calculate derived values
  const derivedValues = calculateDerivedValues();

  // Render table row with values
  const renderTableRow = (
    label: string,
    currentValue: number,
    previousValue: number,
    indent: number = 0,
    isBold: boolean = false,
    isTotal: boolean = false,
    isNegativeRed: boolean = false
  ) => {
    const change = calculateChange(currentValue, previousValue);
    
    return (
      <TableRow 
        sx={{ 
          bgcolor: isTotal ? 'grey.100' : 'inherit',
          '&:hover': { bgcolor: isTotal ? 'grey.200' : 'grey.50' }
        }}
      >
        <TableCell sx={{ 
          pl: indent * 2, 
          fontWeight: isBold ? 600 : 400,
          color: isTotal ? 'primary.main' : 'text.primary'
        }}>
          {label}
        </TableCell>
        <TableCell align="right" sx={{ 
          fontWeight: isBold ? 600 : 400,
          color: isNegativeRed && currentValue < 0 ? '#d32f2f' : 'inherit'
        }}>
          {formatCurrency(currentValue)}
        </TableCell>
        <TableCell align="right" sx={{ 
          fontWeight: isBold ? 600 : 400,
          color: isNegativeRed && previousValue < 0 ? '#d32f2f' : 'inherit'
        }}>
          {formatCurrency(previousValue)}
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            {getTrendIcon(currentValue, previousValue)}
            <Chip
              label={`${change.percent.toFixed(1)}%`}
              size="small"
              color={
                change.value > 0 ? "success" : 
                change.value < 0 ? "error" : "default"
              }
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  // Helper function to render section with formula explanation
  const renderCalculationRow = (
    label: string,
    formula: string,
    currentValue: number,
    previousValue: number,
    indent: number = 1,
    isBold: boolean = true
  ) => {
    const change = calculateChange(currentValue, previousValue);
    
    return (
      <TableRow sx={{ bgcolor: 'grey.50' }}>
        <TableCell sx={{ 
          pl: indent * 2, 
          fontWeight: isBold ? 600 : 400,
          color: 'primary.main'
        }}>
          <Box>
            <Typography variant="body2" fontWeight={isBold ? 600 : 400}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formula}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: isBold ? 600 : 400 }}>
          {formatCurrency(currentValue)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: isBold ? 600 : 400 }}>
          {formatCurrency(previousValue)}
        </TableCell>
        <TableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            {getTrendIcon(currentValue, previousValue)}
            <Chip
              label={`${change.percent.toFixed(1)}%`}
              size="small"
              color={
                change.value > 0 ? "success" : 
                change.value < 0 ? "error" : "default"
              }
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <IonPage className="pageDown1">
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <IonHeader>
          <IonToolbar>
            <Box sx={{
              px: 3,
              py: 1.5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                    PROFIT & LOSS STATEMENT
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Income statement and performance monitoring
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    size="small"
                    onClick={getProfitLossData}
                    disabled={loading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Export PDF">
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    size="small"
                    onClick={() => handleExport('pdf')}
                    disabled={loading}
                  >
                    PDF
                  </Button>
                </Tooltip>

                <Tooltip title="Export Excel">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    size="small"
                    onClick={() => handleExport('excel')}
                    disabled={loading}
                  >
                    Excel
                  </Button>
                </Tooltip>

                <Chip
                  icon={<FilterAltIcon />}
                  label="Filters"
                  size="small"
                  variant="outlined"
                  onClick={() => {/* Open filter dialog */ }}
                />
              </Box>
            </Box>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {loading && <LinearProgress />}

          <Box sx={{
            p: { xs: 2, md: 4 },
            bgcolor: 'background.default',
            minHeight: "100%"
          }}>
            {/* Header Card */}
            <Card sx={{
              mb: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
              color: 'white',
              boxShadow: '0 10px 40px rgba(76, 175, 80, 0.2)'
            }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <BusinessIcon fontSize="large" />
                      <Box>
                        <Typography variant="h4" fontWeight={800}>
                          EDO SPECIALIST HOSPITAL
                        </Typography>
                        <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                          Statement of Profit or Loss
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          For the period ended {formatDateDisplay(selectedDate)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          Current Period
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateDisplay(selectedDate)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          Previous Period
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateDisplay(subYears(selectedDate, 1))}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          Generated On
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {format(new Date(), 'dd MMM yyyy HH:mm')}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Card sx={{ 
                      bgcolor: 'rgba(255,255,255,0.15)', 
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                Total Profit
                              </Typography>
                              <Typography variant="h5" fontWeight={700}>
                                {derivedValues ? formatCurrency(derivedValues.current.totalComprehensiveIncome) : '₦0.00'}
                              </Typography>
                            </Box>
                            <DatePicker
                              label="Period End Date"
                              value={selectedDate}
                              onChange={handleDateChange}
                              slots={{
                                openPickerIcon: CalendarMonthIcon,
                              }}
                              slotProps={{
                                textField: {
                                  size: 'small',
                                  sx: {
                                    '& .MuiInputBase-root': {
                                      bgcolor: 'white',
                                      borderRadius: 2,
                                      minWidth: '150px'
                                    }
                                  }
                                }
                              }}
                            />
                          </Box>
                          {derivedValues && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getTrendIcon(derivedValues.current.totalComprehensiveIncome, derivedValues.previous.totalComprehensiveIncome)}
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {calculateChange(derivedValues.current.totalComprehensiveIncome, derivedValues.previous.totalComprehensiveIncome).percent.toFixed(1)}% from previous period
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Profit & Loss Statement Table */}
            <Fade in={!loading && !!currentData}>
              <Card sx={{ 
                borderRadius: 3, 
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.50' }}>
                        <TableCell sx={{
                          fontWeight: 700,
                          width: '60%',
                          color: 'primary.main',
                          fontSize: '0.95rem',
                          py: 2
                        }}>
                          PROFIT & LOSS STATEMENT
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', py: 2 }}>
                          {format(selectedDate, 'yyyy')} (₦)
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', py: 2 }}>
                          {format(subYears(selectedDate, 1), 'yyyy')} (₦)
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.main', py: 2 }}>
                          CHANGE
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Revenue Section */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 600, color: 'primary.main' }}>
                          REVENUE
                        </TableCell>
                      </TableRow>
                      
                      {currentData && previousData && renderTableRow(
                        "Revenue",
                        currentData.revenue,
                        previousData.revenue,
                        1
                      )}

                      {/* Cost of Services */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 600, color: 'primary.main' }}>
                          COST OF SERVICES
                        </TableCell>
                      </TableRow>
                      
                      {currentData && previousData && renderTableRow(
                        "Cost of services rendered",
                        currentData.cost_of_services,
                        previousData.cost_of_services,
                        1
                      )}

                      {/* Gross Profit Calculation - SUM */}
                      {derivedValues && renderCalculationRow(
                        "Gross profit",
                        "Revenue + Cost of services",
                        derivedValues.current.grossProfit,
                        derivedValues.previous.grossProfit,
                        1
                      )}

                      {/* Operating Income & Expenses */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 600, color: 'primary.main' }}>
                          OPERATING ACTIVITIES
                        </TableCell>
                      </TableRow>

                      {currentData && previousData && renderTableRow(
                        "Other operating income",
                        currentData.operating_income,
                        previousData.operating_income,
                        1
                      )}

                      {currentData && previousData && renderTableRow(
                        "Administrative expenses",
                        currentData.administrative_expenses,
                        previousData.administrative_expenses,
                        1
                      )}

                      {/* Operating Profit Calculation - SUM */}
                      {derivedValues && renderCalculationRow(
                        "Operating profit",
                        "Gross profit + Other operating income + Administrative expenses",
                        derivedValues.current.operatingProfit,
                        derivedValues.previous.operatingProfit,
                        1
                      )}

                      {/* Finance Section */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 600, color: 'primary.main' }}>
                          FINANCE COSTS & INCOME
                        </TableCell>
                      </TableRow>

                      {currentData && previousData && renderTableRow(
                        "Finance income",
                        currentData.finance_income,
                        previousData.finance_income,
                        1
                      )}

                      {currentData && previousData && renderTableRow(
                        "Finance cost",
                        currentData.finance_cost,
                        previousData.finance_cost,
                        1
                      )}

                      {/* Net Finance Cost Calculation - SUM */}
                      {derivedValues && renderCalculationRow(
                        "Net finance cost",
                        "Finance cost + Finance income",
                        derivedValues.current.netFinanceCost,
                        derivedValues.previous.netFinanceCost,
                        1
                      )}

                      {/* Profit Before Tax Calculation - SUM */}
                      {derivedValues && renderCalculationRow(
                        "Profit/(loss) before taxation",
                        "Operating profit + Net finance cost",
                        derivedValues.current.profitBeforeTax,
                        derivedValues.previous.profitBeforeTax,
                        1
                      )}

                      {/* Tax Section */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 600, color: 'primary.main' }}>
                          TAXATION
                        </TableCell>
                      </TableRow>

                      {currentData && previousData && renderTableRow(
                        "Income tax (expense)/credit",
                        currentData.income_tax,
                        previousData.income_tax,
                        1
                      )}

                      {/* Profit After Tax Calculation - SUM */}
                      {derivedValues && renderCalculationRow(
                        "Profit/(loss) after taxation",
                        "Profit before tax + Income tax",
                        derivedValues.current.profitAfterTax,
                        derivedValues.previous.profitAfterTax,
                        1
                      )}

                      {/* Other Comprehensive Income */}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={4} sx={{ py: 1.5, fontWeight: 600, color: 'primary.main' }}>
                          OTHER COMPREHENSIVE INCOME
                        </TableCell>
                      </TableRow>

                      {currentData && previousData && renderTableRow(
                        "Other comprehensive income",
                        currentData.comprehensive_income,
                        previousData.comprehensive_income,
                        1
                      )}

                      {/* Total Comprehensive Income Calculation - SUM */}
                      {derivedValues && renderCalculationRow(
                        "Total comprehensive income/(loss) for the year",
                        "Profit after tax + Other comprehensive income",
                        derivedValues.current.totalComprehensiveIncome,
                        derivedValues.previous.totalComprehensiveIncome,
                        1
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Footer Notes */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    <strong>Notes:</strong> All figures in Nigerian Naira (₦). This statement has been prepared in accordance with IFRS.
                    Report generated on {format(new Date(), 'dd MMMM yyyy')}. All values are summed together as per the requirement.
                  </Typography>
                </Box>
              </Card>
            </Fade>

            {/* Calculation Legend */}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'info.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'info.light'
            }}>
              <Typography variant="subtitle2" fontWeight={600} color="info.main" gutterBottom>
                CALCULATION FORMULAS (ALL SUMS):
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={600}>Gross Profit =</Typography>
                  <Typography variant="caption"> Revenue + Cost of Services</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={600}>Operating Profit =</Typography>
                  <Typography variant="caption"> Gross Profit + Other Operating Income + Administrative Expenses</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={600}>Net Finance Cost =</Typography>
                  <Typography variant="caption"> Finance Cost + Finance Income</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={600}>Profit Before Tax =</Typography>
                  <Typography variant="caption"> Operating Profit + Net Finance Cost</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={600}>Profit After Tax =</Typography>
                  <Typography variant="caption"> Profit Before Tax + Income Tax</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={600}>Total Comprehensive Income =</Typography>
                  <Typography variant="caption"> Profit After Tax + Other Comprehensive Income</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Quick Stats Cards - Updated for Sum Calculations */}
            <Grid container spacing={2} sx={{ mt: 3, mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  height: '100%', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'primary.50',
                        color: 'primary.main'
                      }}>
                        <AttachMoneyIcon />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Revenue
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {currentData ? formatCurrency(currentData.revenue) : '₦0.00'}
                        </Typography>
                        {previousData && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            {getTrendIcon(currentData?.revenue || 0, previousData.revenue)}
                            <Typography 
                              variant="caption" 
                              color={
                                calculateChange(currentData?.revenue || 0, previousData.revenue).value > 0 ? 'success.main' : 
                                calculateChange(currentData?.revenue || 0, previousData.revenue).value < 0 ? 'error.main' : 'text.secondary'
                              }
                            >
                              {calculateChange(currentData?.revenue || 0, previousData.revenue).percent.toFixed(1)}% from previous
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  height: '100%', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: derivedValues?.current.operatingProfit || 0 > 0 ? 'success.50' : 'error.50',
                        color: derivedValues?.current.operatingProfit || 0 > 0 ? 'success.main' : 'error.main'
                      }}>
                        <AssessmentIcon />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Operating Profit
                        </Typography>
                        <Typography variant="h6" fontWeight={700} 
                          color={derivedValues?.current.operatingProfit || 0 > 0 ? 'success.main' : 'error.main'}>
                          {derivedValues ? formatCurrency(derivedValues.current.operatingProfit) : '₦0.00'}
                        </Typography>
                        {derivedValues && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            {getTrendIcon(derivedValues.current.operatingProfit, derivedValues.previous.operatingProfit)}
                            <Typography 
                              variant="caption" 
                              color={
                                calculateChange(derivedValues.current.operatingProfit, derivedValues.previous.operatingProfit).value > 0 ? 'success.main' : 
                                calculateChange(derivedValues.current.operatingProfit, derivedValues.previous.operatingProfit).value < 0 ? 'error.main' : 'text.secondary'
                              }
                            >
                              {calculateChange(derivedValues.current.operatingProfit, derivedValues.previous.operatingProfit).percent.toFixed(1)}% from previous
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  height: '100%', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: derivedValues?.current.totalComprehensiveIncome || 0 > 0 ? 'success.light' : 'error.light',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: derivedValues?.current.totalComprehensiveIncome || 0 > 0 ? 'success.50' : 'error.50',
                        color: derivedValues?.current.totalComprehensiveIncome || 0 > 0 ? 'success.main' : 'error.main'
                      }}>
                        {derivedValues?.current.totalComprehensiveIncome || 0 > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Total Comprehensive Income
                        </Typography>
                        <Typography variant="h6" fontWeight={700} 
                          color={derivedValues?.current.totalComprehensiveIncome || 0 > 0 ? 'success.main' : 'error.main'}>
                          {derivedValues ? formatCurrency(derivedValues.current.totalComprehensiveIncome) : '₦0.00'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {derivedValues?.current.totalComprehensiveIncome || 0 > 0 ? 'Profitable' : 'Loss Making'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Legend */}
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="caption" fontWeight={600} color="text.primary" sx={{ mb: 1, display: 'block' }}>
                LEGEND:
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="success" fontSize="small" />
                  <Typography variant="caption">Increase from previous year</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDownIcon color="error" fontSize="small" />
                  <Typography variant="caption">Decrease from previous year</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  <Typography variant="caption">Section header</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </IonContent>
      </LocalizationProvider>
    </IonPage>
  );
};

export default ProfitAndLoss;